import { type ReactNode } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { AppLoader } from './AppLoader'
import AppAccessDeniedPage from '../pages/AppAccessDeniedPage'

/** Gates an app's whole shell on the signed-in user actually being scoped to
 *  it (owner/admin/superuser always pass; anyone else needs an explicit
 *  app_scope grant — see usePermissions.inApp). Sits INSIDE RequireAuth: by
 *  the time this renders, the user is authenticated, just maybe not
 *  entitled to this particular product app.
 *
 *  `fallback` replaces the default "ask an admin" wall. Most apps want the
 *  wall: there is genuinely nothing a person without the grant can do there.
 *  A DEVICE app is the exception — see apps/pocket, where the way in is to
 *  pair the hardware, which needs no personal entitlement at all. */
export default function RequireAppAccess({
  appKind, appLabel, children, fallback,
}: {
  appKind: string
  appLabel: string
  children: ReactNode
  fallback?: ReactNode
}) {
  const { user, loading } = useAuth()
  const { inApp } = usePermissions()

  // /auth/me hasn't resolved yet — avoid a flash of "access denied" before we
  // actually know the user's app_scope.
  if (loading || !user) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background">
        <AppLoader />
      </div>
    )
  }

  if (!inApp(appKind)) {
    return <>{fallback ?? <AppAccessDeniedPage appLabel={appLabel} />}</>
  }

  return <>{children}</>
}
